/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, IButton, IconButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { ITextProps, ITextStyles } from '@fluentui/react/lib/components/Text';
import { DetailsRow, IColumn, IDetailsList, IDetailsRowProps, IDetailsRowStyles } from '@fluentui/react/lib/DetailsList';
import { IDropdown } from '@fluentui/react/lib/Dropdown';
import { FontIcon } from '@fluentui/react/lib/Icon';
import { IStackProps, IStackStyles, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { logButtonClickWithCustomData } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { noop, noopUndefined } from 'src/core/utils/base';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { QnaMakerAnswerColumnRenderer } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerAnswerColumnRenderer';
import { QnaMakerQuestionsColumnRenderer } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerQuestionsColumnRenderer';
import { getFlattenMetadata } from 'src/qnaMaker/appShell/qnaMakerEditPage/qnaMakerFiltersUtils';
import { QnaMakerQnaPairsBeingEditedStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerQnaPairsBeingEditedStore';
import { QnaMakerMetadata } from 'src/qnaMaker/core/components/QnaMakerMetadata';
import { useQnaMetricActions } from 'src/qnaMaker/core/hooks/useQnaMetricActions';
import { QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerQnaStore } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';
import { QnaMakerSourceStore } from 'src/qnaMaker/core/stores/QnaMakerSourceStore';
import { QnaPatchOperation } from 'src/qnaMaker/core/types/qnaPatchOperation';

export type QnaData = Omit<QnaMakerQnaSwagger, 'lastUpdatedDateTime'> & { linkedPair?: Partial<QnaMakerQnaSwagger> };

export type QnaMakerQnaRowRendererProps = {
    columns?: IColumn[];
    getNewPairId?: () => number;
    rowProps?: IDetailsRowProps;
    isRichTextEnabled?: boolean;
    isAddActionDisabled: boolean;
    openedPromptModalId: number;
    qnaPairsJson: QnaMakerQnaSwagger[];
    sourceStores: QnaMakerSourceStore[];
    onDismissChanges: (id: number) => void;
    onOpenedPromptModalChange: (value: number) => void;
    onSubmitChanges: (newData: Partial<QnaData>) => void;
    dirtyQnaPairs?: QnaPatchOperation<QnaMakerQnaSwagger>[];
    qnaPairsBeingEditedStore: QnaMakerQnaPairsBeingEditedStore;
    setDetailsQnaPairId?: (detailsQnaPairId: number) => void;
    isExpandView?: boolean;
    tableRef?: React.MutableRefObject<IDetailsList>;
};

const messages = defineMessages({
    source: {
        id: 'qnaMaker.qnaMakerQnaRowRenderer.source',
        defaultMessage: 'Source'
    },
    selectSource: {
        id: 'qnaMaker.qnaMakerQnaRowRenderer.selectSource',
        defaultMessage: 'Select source combobox'
    },
    submit: {
        id: 'qnaMaker.qnaMakerQnaRowRenderer.submit',
        defaultMessage: 'Submit'
    },
    cancel: {
        id: 'qnaMaker.qnaMakerQnaRowRenderer.cancel',
        defaultMessage: 'Cancel'
    },
    contextPlaceholder: {
        id: 'qnaMaker.qnaMakerQnaRowRenderer.contextPlaceholder',
        defaultMessage: 'This field is not editable'
    },
    expandIconButtonAriaLabel: {
        id: 'qnaMaker.qnaMakerQnaRowRenderer.expandIconButtonAriaLabel',
        defaultMessage: 'expand to question pair detail page'
    },
    expandIconButtonDisabledTooltip: {
        id: 'qnaMaker.qnaMakerQnaRowRenderer.expandIconButtonDisabledTooltip',
        defaultMessage: 'Save changes to view details'
    }
});

const localTheme = getScopedTheme('QnaMakerQnaRowRenderer');
const { getClassName, styleComponent } = getStylistV2('QnaMakerQnaRowRenderer');

const iconClassName = getClassName('iconClassName', () => ({ fontSize: '14px', color: localTheme.secondaryColor }));
const linkStyles = classNamesFunction<ITextProps, ITextStyles>()({ root: { fontStyle: 'italic' } });
const RowContainer = styleComponent<IStackProps>(Stack)(
    'RowContainer',
    (theme, cssVars) => ({
        borderBottom: `${theme.focussingColor} solid 1px`,
        backgroundColor: cssVars.defaultBackground,
        $nest: {
            '&:hover': { backgroundColor: cssVars.hoveringBackground }
        }
    }),
    {
        cssVars: { hoveringBackground: '', defaultBackground: '' },
        cssVarsSetter: ({ isSelected, isInAddMode }: { isSelected: boolean; isInAddMode: boolean }) => ({
            hoveringBackground: `${isSelected ? localTheme.focussingColor : localTheme.hoveringColor}`,
            defaultBackground: `${isInAddMode ? localTheme.focussingColor : isSelected ? localTheme.hoveringColor : 'unset'}`
        })
    }
);

const detailsRowStyles = classNamesFunction<IDetailsRowProps, IDetailsRowStyles>()({
    root: {
        borderBottom: 'none',
        backgroundColor: 'transparent !important',
        userSelect: 'auto'
    },
    cell: {
        overflow: 'visible'
    },
    check: {
        cursor: 'pointer !important'
    }
});

const expandViewEditModeStyles = classNamesFunction<IDetailsRowProps, IDetailsRowStyles>()({
    root: { minHeight: '380px', height: '100%', position: 'relative' }
});

const expandViewStyles = classNamesFunction<IStackProps, IStackStyles>()({
    root: { minHeight: '430px', height: '100%', position: 'relative' }
});

/**
 * A component responsible for rendering the table row in
 * three different views:
 * 1. Regular view
 * 2. Edit mode view
 * 3. Add new Qna view
 */
export const QnaMakerQnaRowRenderer = (props: QnaMakerQnaRowRendererProps) => {
    const {
        rowProps,
        qnaPairsJson,
        sourceStores,
        onSubmitChanges,
        onDismissChanges,
        dirtyQnaPairs = [],
        isAddActionDisabled,
        openedPromptModalId,
        onOpenedPromptModalChange,
        qnaPairsBeingEditedStore,
        isRichTextEnabled = false,
        setDetailsQnaPairId = noop,
        getNewPairId = noopUndefined,
        isExpandView = false,
        tableRef
    } = props;
    const { localize } = useLocalization();
    const history = useHistory();
    const { serviceName } = getPortalFromUrl();
    const { rootStore } = useGlobalState();
    const { id: currentProjectId } = rootStore.currentQnaMakerProjectStore;

    const item: QnaData = rowProps?.item as QnaData;
    const isPromptModalHidden = openedPromptModalId !== item?.id;
    const isInAddMode: boolean = !item;
    const isInEditMode: boolean = Boolean(qnaPairsBeingEditedStore.getPair(item?.id));
    const isCurrentItemSelected = rowProps?.selection.getSelectedIndices().includes(rowProps.itemIndex);

    const defaultItem: QnaData = {
        id: getNewPairId(),
        dialog: { isContextOnly: false, prompts: [] },
        questions: [],
        metadata: {},
        source: '',
        isDocumentText: false,
        answer: ''
    };

    const {
        getRowInfoOnSubmit,
        setTimestampToStartEditing,
        increaseNumberOfClicks,
        setPairOperationType,
        resetRowMetrics
    } = useQnaMetricActions();

    const [qnaPairData, updateQnaPairData] = useUpdateState<QnaData>(() => {
        if (isInEditMode) {
            return qnaPairsBeingEditedStore.getPair(item.id) as QnaData;
        }

        return item || defaultItem;
    });

    const [hasQnaPairDataBeenModified, setHasQnaPairDataBeenModified] = useState<boolean>(false);

    React.useEffect(() => {
        if (item) {
            if (isInEditMode) {
                updateQnaPairData(qnaPairsBeingEditedStore.getPair(item.id));
            } else {
                updateQnaPairData(item);
            }
        }
    }, [item, isInEditMode]);

    React.useEffect(() => setHasQnaPairDataBeenModified(false), [isInEditMode]);

    React.useEffect(() => {
        setPairOperationType(item?.id ?? getNewPairId(), isInEditMode ? 'edit' : isInAddMode ? 'add' : 'none');

        if (isInEditMode) {
            setTimestampToStartEditing(item.id, Date.now());
        }
        if (isInAddMode) {
            setTimestampToStartEditing(getNewPairId(), Date.now());
        }
    }, [isInAddMode, isInEditMode]);

    const onRenderQuestionsColumnInAddOrEditModes = () => (
        <QnaMakerQuestionsColumnRenderer
            isInEditMode
            isInAddMode
            pairId={qnaPairData.id}
            questions={qnaPairData.questions}
            onEditQuestions={(questions, activeLearningSuggestions) => {
                if (isInEditMode) {
                    qnaPairsBeingEditedStore.updatePair({
                        ...qnaPairData,
                        questions,
                        activeLearningSuggestions
                    });
                }
                updateQnaPairData({ questions, activeLearningSuggestions });
                setHasQnaPairDataBeenModified(true);
            }}
            isAddActionDisabled={isAddActionDisabled}
        />
    );

    const onRenderAnswerColumnInEditOrAddModes = () => (
        <QnaMakerAnswerColumnRenderer
            isInEditMode
            pairId={qnaPairData.id}
            qnaPairsJson={qnaPairsJson}
            answer={qnaPairData.answer}
            selectedQnaId={qnaPairData.id}
            prompts={qnaPairData.dialog.prompts}
            isRichTextEnabled={isRichTextEnabled}
            isAddActionDisabled={isAddActionDisabled}
            isPromptModalHidden={isPromptModalHidden}
            setIsPromptModalHidden={val => onOpenedPromptModalChange(val ? null : item?.id)}
            isContextOnly={qnaPairData.dialog.isContextOnly}
            onChange={(answer, prompts, modifiedPair) => {
                if (isInEditMode) {
                    qnaPairsBeingEditedStore.updatePair({
                        ...qnaPairData,
                        answer,
                        dialog: {
                            prompts: prompts.map((p, i) => ({ ...p, displayOrder: i })),
                            isContextOnly: qnaPairData.dialog.isContextOnly
                        }
                    });
                }
                updateQnaPairData({
                    answer,
                    dialog: {
                        prompts: prompts.map((p, i) => ({ ...p, displayOrder: i })),
                        isContextOnly: qnaPairData.dialog.isContextOnly
                    },
                    // existing pairs needs to modify its questions or context flag
                    linkedPair: modifiedPair
                });
                setHasQnaPairDataBeenModified(true);
            }}
        />
    );

    const onRenderMetadataColumnInEditOrAddModes = () => (
        <QnaMakerMetadata
            isInEditMode
            pairId={qnaPairData.id}
            metadata={qnaPairData.metadata}
            isAddActionDisabled={isAddActionDisabled}
            onChangeMetadata={metadata => {
                if (isInEditMode) {
                    qnaPairsBeingEditedStore.updatePair({
                        ...qnaPairData,
                        metadata
                    });
                }
                updateQnaPairData({ metadata });
                setHasQnaPairDataBeenModified(true);
            }}
            flattenMetadata={getFlattenMetadata(qnaPairsJson)}
        />
    );
    const getColumnContent = (columnKey: string): (() => JSX.Element) => {
        switch (columnKey) {
            case 'context':
                return () => <LuisText>{localize(messages.contextPlaceholder)}</LuisText>;
            case 'questions':
                return onRenderQuestionsColumnInAddOrEditModes;
            case 'answerAndPrompts':
                return onRenderAnswerColumnInEditOrAddModes;
            case 'metadata':
                return onRenderMetadataColumnInEditOrAddModes;
            default:
                return () => null;
        }
    };

    const editModeColumns = rowProps?.columns.map(column => ({
        ...column,
        onRender: getColumnContent(column.key)
    }));

    const sourceRowHeight = 28;

    const sourceOptions = sourceStores.map(src => ({ key: src.source, text: src.source }));
    if (!sourceOptions.some(src => src.key === 'Editorial')) {
        sourceOptions.unshift({ key: 'Editorial', text: 'Editorial' });
    }

    const computeSubmitButtonDisabledState = () => {
        if (!hasQnaPairDataBeenModified && !isInAddMode) {
            return true;
        }

        const validAnswer = qnaPairData.answer.trim() || qnaPairData.dialog?.prompts.length;
        const validQuestions = qnaPairData.questions?.[0]?.trim();

        return !validAnswer || !validQuestions;
    };

    const onSubmit = () => {
        logButtonClickWithCustomData(serviceName, 'EditKB', 'Submit', {
            pivotName: 'Qnas',
            projectId: currentProjectId,
            ...getRowInfoOnSubmit(item?.id ?? getNewPairId())
        });
        increaseNumberOfClicks();
        resetRowMetrics(item?.id ?? getNewPairId());
        onSubmitChanges({ ...qnaPairData });
    };

    const onDismiss = () => {
        if (isInEditMode) {
            qnaPairsBeingEditedStore.deletePair(item.id);
        }

        if (isInAddMode || isInEditMode) {
            resetRowMetrics(item?.id ?? getNewPairId());
            increaseNumberOfClicks();
        }

        updateQnaPairData(item);
        onDismissChanges(item?.id);
    };

    const handleSourcesDropdownChange = (source: string) => {
        if (isInEditMode) {
            qnaPairsBeingEditedStore.updatePair({
                ...qnaPairData,
                source
            });
        }
        updateQnaPairData({ source });
        setHasQnaPairDataBeenModified(true);
    };

    const handleDetailsPageOpen = (qnaPair: QnaMakerQnaStore) => {
        setDetailsQnaPairId(qnaPair.id);
        history.push(routeTable.qnaMaker.detailsPage(currentProjectId, String(qnaPair.id)));
    };

    const sourcesDropdownIdRef = React.useRef<IDropdown>(null);
    const submitButtonIdRef = React.useRef<IButton>(null);
    const cancelButtonIdRef = React.useRef<IButton>(null);

    const handleDropdownKeyDown = (e: React.KeyboardEvent<HTMLDivElement | HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === LuisKeyCodes.ArrowUp) {
            e.preventDefault();
            e.stopPropagation();
            const tableRows = document.querySelectorAll('[role="row"]');
            for (let index = 1; index < tableRows.length; index++) {
                const row = tableRows[index] as HTMLElement;
                const isSelected = row.getAttribute('aria-selected');
                if (isSelected === 'true') {
                    tableRef?.current?.scrollToIndex(index - 1);
                    row.focus();
                    return;
                }
            }

            (tableRows[1] as HTMLElement).focus();
        } else if (e.key === LuisKeyCodes.ArrowRight) {
            if (computeSubmitButtonDisabledState()) {
                cancelButtonIdRef.current?.focus();
            } else {
                submitButtonIdRef.current?.focus();
            }
        } else if (e.key === LuisKeyCodes.ArrowDown) {
            e.preventDefault();
        }
    };

    const handleSubmitKeyDown = (e: React.KeyboardEvent<HTMLDivElement | HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === LuisKeyCodes.ArrowLeft) {
            sourcesDropdownIdRef.current?.focus();
        } else if (e.key === LuisKeyCodes.ArrowRight) {
            cancelButtonIdRef.current?.focus();
        } else if (e.key === LuisKeyCodes.ArrowUp) {
            e.preventDefault();
            e.stopPropagation();
            const tableRows = document.querySelectorAll('[role="row"]');
            for (let index = 1; index < tableRows.length; index++) {
                const row = tableRows[index] as HTMLElement;
                const isSelected = row.getAttribute('aria-selected');
                if (isSelected === 'true') {
                    tableRef?.current?.scrollToIndex(index - 1);
                    row.focus();
                    return;
                }
            }

            (tableRows[1] as HTMLElement).focus();
        } else if (e.key === LuisKeyCodes.ArrowDown) {
            e.preventDefault();
        }
    };

    const handleCancelKeyDown = (e: React.KeyboardEvent<HTMLDivElement | HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === LuisKeyCodes.ArrowLeft) {
            if (computeSubmitButtonDisabledState()) {
                sourcesDropdownIdRef.current?.focus();
            } else {
                submitButtonIdRef.current?.focus();
            }
        } else if (e.key === LuisKeyCodes.ArrowUp) {
            e.preventDefault();
            e.stopPropagation();
            const tableRows = document.querySelectorAll('[role="row"]');
            for (let index = 1; index < tableRows.length; index++) {
                const row = tableRows[index] as HTMLElement;
                const isSelected = row.getAttribute('aria-selected');
                if (isSelected === 'true') {
                    tableRef?.current?.scrollToIndex(index - 1);
                    row.focus();
                    return;
                }
            }

            (tableRows[1] as HTMLElement).focus();
        } else if (e.key === LuisKeyCodes.ArrowDown) {
            e.preventDefault();
        }
    };

    return (
        <RowContainer
            id={`qna_${qnaPairData?.id}`}
            customProps={{ isSelected: isCurrentItemSelected, isInAddMode }}
            tokens={{ childrenGap: 32 }}>
            {isInAddMode ? (
                <DetailsRow role="row" item={{}} itemIndex={-1} columns={editModeColumns} styles={detailsRowStyles} />
            ) : isInEditMode ? (
                <DetailsRow
                    {...rowProps}
                    columns={editModeColumns}
                    styles={isExpandView ? mergeStyleSets(detailsRowStyles, expandViewEditModeStyles) : detailsRowStyles}
                />
            ) : (
                <Stack horizontal styles={isExpandView ? expandViewStyles : undefined}>
                    <DetailsRow {...rowProps} styles={detailsRowStyles} />
                    {setDetailsQnaPairId !== noop && FeatureToggles.QnaPairDetais && (
                        <LuisTooltip
                            hidden={!dirtyQnaPairs.some(qp => qp.op === 'add' && qp.value.id === rowProps.item.id)}
                            content={localize(messages.expandIconButtonDisabledTooltip)}>
                            <IconButton
                                iconProps={{ iconName: 'ChromeFullScreen' }}
                                onClick={() => handleDetailsPageOpen(rowProps.item)}
                                ariaLabel={localize(messages.expandIconButtonAriaLabel)}
                                disabled={dirtyQnaPairs.some(qp => qp.op === 'add' && qp.value.id === rowProps.item.id)}
                            />
                        </LuisTooltip>
                    )}
                </Stack>
            )}

            <Stack horizontal verticalAlign="baseline" verticalFill tokens={{ childrenGap: 16, padding: '0 10px 20px 60px' }}>
                <Stack horizontal verticalAlign="center" styles={{ root: { width: '100%' } }}>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                        <FontIcon iconName="Source" className={iconClassName} />
                        <LuisText variant="medium" fontWeight={600} color={localTheme.secondaryColor}>
                            {localize(messages.source)}:
                        </LuisText>
                    </Stack>
                    &nbsp;
                    {isInEditMode || isInAddMode ? (
                        <Stack horizontal verticalAlign="center" horizontalAlign="space-between" grow>
                            <LuisDropdown
                                options={sourceOptions}
                                onKeyDown={handleDropdownKeyDown}
                                componentRef={sourcesDropdownIdRef}
                                aria-label={localize(messages.selectSource)}
                                selectedKey={qnaPairData.source || 'Editorial'}
                                styles={{ root: { height: sourceRowHeight, width: '300px' } }}
                                onChange={(_, option) => handleSourcesDropdownChange(option.key as string)}
                            />
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                                <PrimaryButton
                                    data-automation-id="primaryButton"
                                    onClick={onSubmit}
                                    onKeyDown={handleSubmitKeyDown}
                                    componentRef={submitButtonIdRef}
                                    text={localize(messages.submit)}
                                    disabled={computeSubmitButtonDisabledState()}
                                    styles={{ root: { height: sourceRowHeight } }}
                                />
                                <DefaultButton
                                    onClick={onDismiss}
                                    onKeyDown={handleCancelKeyDown}
                                    componentRef={cancelButtonIdRef}
                                    text={localize(messages.cancel)}
                                    styles={{ root: { height: sourceRowHeight } }}
                                />
                            </Stack>
                        </Stack>
                    ) : (
                        <TruncatedText
                            variant="medium"
                            title={qnaPairData.source}
                            color={localTheme.secondaryColor}
                            styles={linkStyles}
                            maxWidth="70%">
                            {qnaPairData.source || 'Editorial'}
                        </TruncatedText>
                    )}
                </Stack>
            </Stack>
        </RowContainer>
    );
};
